import { memo, useEffect, useRef } from "react";
import {
  RemoteParticipant,
  RemoteVideoTrack,
  Room as RoomType,
} from "twilio-video";
import { executeFacialRecognition } from "../../../api/rooms/facial-recognition/executeFacialRecognition";
import { getConfiguration } from "../../../api/rooms/facial-recognition/getConfiguration";
import { IBioDocConfig } from "../../../common/interfaces/IBioDocConfig";
import { useInfo } from "../../../hooks/useInfo";
import { useRemoteParticipant } from "../../../hooks/useRemoteParticipant";
import { showToast } from "../../../utils/showToast";
import { getExistingVideoTrack } from "../../helpers/getExistingTrack";

interface IFacialRecognitionHandlerProps {
  room: RoomType;
}

const FacialRecognitionHandler = ({ room }: IFacialRecognitionHandlerProps) => {
  const { telemedicineData, bioDocConfig, handleSetBioDocConfig } = useInfo();
  const { remoteParticipant } = useRemoteParticipant();
  const isFacialRecognitionDone = useRef<boolean>(false);

  const handleFacialRecognition = (
    bioDoc?: IBioDocConfig,
    participant?: RemoteParticipant
  ) => {
    const part = participant || remoteParticipant;
    if (part) {
      showToast("info", {
        text: "Efetuando tentativa de reconhecimento facial do beneficiário",
        showConfirmButton: true,
        confirmButtonText: "OK",
        timer: 5000,
      });
      executeFacialRecognition(
        bioDoc || bioDocConfig,
        getExistingVideoTrack(part) as RemoteVideoTrack[]
      ).then((response) => {
        isFacialRecognitionDone.current = response.success;
      });
    }
  };

  const handleParticipantConnected = (participant: RemoteParticipant) => {
    if (telemedicineData.permission === "professional") {
      getConfiguration().then((response) => {
        if (response) {
          setTimeout(() => {
            handleSetBioDocConfig(response.data);
            if (response.data.active && !response.data.isFacialIdDone) {
              handleFacialRecognition(response.data, participant);
            }
          }, 5000);
        }
      });
    }
  };

  const handleRemoveBioDocConfig = () => {
    if (telemedicineData.permission === "professional") {
      handleSetBioDocConfig({} as IBioDocConfig);
    }
  };

  useEffect(() => {
    room.on("participantConnected", handleParticipantConnected);
    room.on("participantDisconnected", handleRemoveBioDocConfig);
    room.participants.forEach(handleParticipantConnected);

    return () => {
      room.removeListener("participantConnected", handleParticipantConnected);
      room.removeListener("participantDisconnected", handleRemoveBioDocConfig);
    };
  }, [room]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (
        bioDocConfig.active &&
        !bioDocConfig.isFacialIdDone &&
        !isFacialRecognitionDone.current
      ) {
        handleFacialRecognition();
      } else {
        clearInterval(interval);
      }
    }, 60000);

    return () => clearInterval(interval);
  }, [bioDocConfig]);

  return null;
};

export default memo(FacialRecognitionHandler);
